/**
 * Comic Decryption Utility
 * Decrypts comic images encrypted with AES-256-GCM
 * Pages are fetched from GitHub, decrypted in the browser.
 */

// Remote host for all comic pages
const PAID_BASE  = 'https://raw.githubusercontent.com/elchinoga/FootParadise/main';
const FREE_BASE  = PAID_BASE;

// Encryption key (AES-256-GCM)
const _k = [249,130,133,205,113,102,121,26,252,27,47,95,137,245,50,183,184,3,134,91,133,212,89,210,90,87,165,88,150,160,87,199];
const ENCRYPTION_KEY = new Uint8Array(_k);
const IV_LENGTH       = 12;
const AUTH_TAG_LENGTH = 16;

// In-memory blob URL cache (cleared on page reload)
const imageCache = new Map();

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve a local relative path to its remote GitHub raw URL.
 * Covers and pages are both fetched from GitHub — nothing is bundled locally.
 */
export function resolvePageUrl(localPath) {
  if (localPath.startsWith('http')) return localPath; // already absolute

  // Paths already under /comics/ → map directly
  if (localPath.startsWith('/comics/')) return PAID_BASE + localPath;

  // Root-level paths like /comic1_page1.jpg → moved to /comics/ in the repo
  return PAID_BASE + '/comics' + localPath;
}

// ─── Crypto ──────────────────────────────────────────────────────────────────

async function decryptBuffer(encryptedBuffer) {
  const iv          = encryptedBuffer.slice(0, IV_LENGTH);
  const authTag     = encryptedBuffer.slice(IV_LENGTH, IV_LENGTH + AUTH_TAG_LENGTH);
  const encData     = encryptedBuffer.slice(IV_LENGTH + AUTH_TAG_LENGTH);
  const key         = await crypto.subtle.importKey('raw', ENCRYPTION_KEY, { name: 'AES-GCM' }, false, ['decrypt']);
  const combined    = new Uint8Array(encData.length + authTag.length);
  combined.set(encData);
  combined.set(authTag, encData.length);
  const decrypted   = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, combined);
  return new Uint8Array(decrypted);
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Load a comic page from GitHub, decrypting it in the browser.
 * Handles both encrypted (.enc) and plain (.jpg) pages transparently.
 *
 * @param {string} originalUrl - The imageUrl from comics.js (relative path, .jpg or .enc)
 * @returns {Promise<string>} Blob URL of the ready-to-display image
 */
export async function loadComicPage(originalUrl) {
  if (imageCache.has(originalUrl)) return imageCache.get(originalUrl);

  const remoteUrl = resolvePageUrl(originalUrl);

  // For encrypted pages: try .enc version first
  const isEnc    = originalUrl.endsWith('.enc') || remoteUrl.endsWith('.enc');
  const encUrl   = isEnc ? remoteUrl : toEncryptedUrl(remoteUrl);
  const plainUrl = isEnc ? null : remoteUrl;

  // 1. Fetch encrypted version
  try {
    const resp = await fetch(encUrl);
    if (resp.ok) {
      const data = new Uint8Array(await resp.arrayBuffer());
      const dec  = await decryptBuffer(data);
      const url  = URL.createObjectURL(new Blob([dec], { type: 'image/jpeg' }));
      imageCache.set(originalUrl, url);
      return url;
    }
  } catch {}

  // 2. Fallback: plain .jpg (comics 0, 14, 15 — not encrypted)
  if (plainUrl) {
    const resp = await fetch(plainUrl);
    if (resp.ok) {
      const data = new Uint8Array(await resp.arrayBuffer());
      const url  = URL.createObjectURL(new Blob([data], { type: 'image/jpeg' }));
      imageCache.set(originalUrl, url);
      return url;
    }
  }

  throw new Error(`Failed to load comic page: ${originalUrl}`);
}

/**
 * Legacy function kept for compatibility.
 * Prefer loadComicPage() for new code.
 */
export async function decryptImage(encryptedUrl) {
  if (imageCache.has(encryptedUrl)) return imageCache.get(encryptedUrl);
  const remoteUrl = resolvePageUrl(encryptedUrl);
  const resp = await fetch(remoteUrl);
  if (!resp.ok) throw new Error(`Failed to fetch: ${remoteUrl}`);
  const data = new Uint8Array(await resp.arrayBuffer());
  const dec  = await decryptBuffer(data);
  const url  = URL.createObjectURL(new Blob([dec], { type: 'image/jpeg' }));
  imageCache.set(encryptedUrl, url);
  return url;
}

export function toEncryptedUrl(imageUrl) {
  return imageUrl.replace(/\.(jpg|jpeg|png)$/i, '.enc');
}

export function clearImageCache() {
  for (const blobUrl of imageCache.values()) URL.revokeObjectURL(blobUrl);
  imageCache.clear();
}

export async function preloadImages(imageUrls) {
  await Promise.all(imageUrls.map(url => loadComicPage(url)));
}
